/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {useCallback, useContext, useRef, useState} from 'react';
import {ChangeFunction} from "../util/interfaces";
import {generateComponentId} from "../util/componentUtil";
import {ControlContext} from "../util/controlContext";
import DvrdRadio from "./dvrdRadio";

interface Props {
    onChange: ChangeFunction<string | number | bigint>;
    value: string | number | bigint;
    checked: boolean;
    disabled?: boolean;
    label?: string;
    labelPosition?: 'left' | 'right';
    baseColor?: string;
    id?: string;
    title?: string;
    className?: string;
}

export default function DvrdRadioController(props: Props) {
    const context = useContext(ControlContext);
    const {disabled, onChange, value, checked, labelPosition, label, className, baseColor, title} = props;
    const [hasHover, setHasHover] = useState(false);
    const id = useRef(generateComponentId(props.id));

    const getContainer = useCallback(() => {
        return document.getElementById(id.current);
    }, [id.current]);

    const getRipple = useCallback(() => {
        const container = getContainer();
        if (container === null) return null;
        return container.querySelector('div.ripple');
    }, [id.current]);

    function onClick() {
        if (disabled) return;
        onChange(value);
        if (!context.noAnimations)
            addRipple();
    }

    function onMouseEnter() {
        if (!disabled)
            setHasHover(true);
    }

    function onMouseLeave() {
        setHasHover(false);
    }

    function addRipple() {
        const container = getContainer();
        const ripple = this.getRipple();
        if (!container || !ripple) return;
        if (container.classList.contains('rippling'))
            container.classList.remove('rippling');
        ripple.addEventListener('transitionend', removeRipple);
        container.classList.add('rippling');
    }

    function removeRipple() {
        const ripple = getRipple();
        const container = getContainer();
        if (!container || !ripple) return;
        ripple.removeEventListener('transitionend', this.removeRipple);
        container.classList.remove('rippling');
    }

    return (
        <DvrdRadio onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} checked={checked}
                   hasHover={hasHover} id={id.current} labelPosition={labelPosition ?? 'right'} label={label}
                   baseColor={baseColor} className={className} title={title} disabled={disabled}/>
    );
}